'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@heroui/button';
import { Input } from '@heroui/input';
import { Spinner } from '@heroui/spinner';
import Select from 'react-select';
import { UserIcon, UsersIcon, XCircleIcon } from '@phosphor-icons/react';
import { TzSelectStyles } from '@/shared/styleDefs';
import { TenantDTO, TIMEZONE_OPTIONS, UpdateTenantRequest, CSMBulkGrantAccessResponse, ActiveCSMUserListResponse, CSMUserSummaryDTO, CSMTenantAccessDTO } from '@/shared/types';
import { TenantBannersAndRemoteStorage } from './TenantBannersAndRemoteStorage';
import { validateRemoteStorage, sanitizeBannersAndRemoteStorage } from './tenant-form-shared';
import { getTokenForHeimdallAdmin } from '@/lib/heimdall-admin-token';
import { listActiveCSMs, grantCSMTenantAccess, grantAllCSMsTenantAccess, revokeCSMTenantAccess } from '@/lib/admin-csm-access';
export interface EditTenantFormProps {
  tenant: TenantDTO;
  idToken: string;
  onSubmit: (tenantId: string, data: UpdateTenantRequest) => Promise<TenantDTO>;
  onSuccess: (tenant: TenantDTO) => void;
  onCancel: () => void;
}
export function EditTenantForm({
  tenant,
  idToken,
  onSubmit,
  onSuccess,
  onCancel
}: EditTenantFormProps) {
  // ── Form state ────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState<UpdateTenantRequest>({
    tenant_name: tenant.tenant_name,
    timezone: tenant.timezone,
    banners: tenant.banners ?? [],
    remote_storage_type: tenant.remote_storage_type,
    remote_storage_url: tenant.remote_storage_url ?? '',
    multi_tenant_friendly_name: tenant.multi_tenant_friendly_name ?? ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setFormData({
      tenant_name: tenant.tenant_name,
      timezone: tenant.timezone,
      banners: tenant.banners ?? [],
      remote_storage_type: tenant.remote_storage_type,
      remote_storage_url: tenant.remote_storage_url ?? '',
      multi_tenant_friendly_name: tenant.multi_tenant_friendly_name ?? ''
    });
  }, [tenant]);

  // ── CSM state (single fetch drives both panels) ───────────────────────────
  const [availableActiveCsmList, setAvailableActiveCsmList] = useState<CSMUserSummaryDTO[]>([]);
  const [assignedCsmList, setAssignedCsmList] = useState<CSMUserSummaryDTO[]>([]);
  const [csmLoading, setCsmLoading] = useState(false);
  const [csmError, setCsmError] = useState<string | null>(null);
  const [showGrantInput, setShowGrantInput] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [grantResult, setGrantResult] = useState<CSMTenantAccessDTO | null>(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revokeResult, setRevokeResult] = useState<CSMTenantAccessDTO | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<CSMBulkGrantAccessResponse | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // ── Fetch all active CSMs — split into assigned/available for this tenant ─
  const fetchCsmData = useCallback(async () => {
    setCsmLoading(true);
    setCsmError(null);
    try {
      const token = await getTokenForHeimdallAdmin(idToken);
      if (!token) throw new Error('Not authenticated');
      const res: ActiveCSMUserListResponse = await listActiveCSMs(token);
      const list = res.csm_list ?? [];
      setAvailableActiveCsmList(list.filter(u => u.is_active && !u.accessible_tenants.includes(tenant.id)));
      setAssignedCsmList(list.filter(u => u.is_active && u.accessible_tenants.includes(tenant.id)));
    } catch (err) {
      setCsmError(err instanceof Error ? err.message : 'Failed to load CSM data');
    } finally {
      setCsmLoading(false);
    }
  }, [idToken, tenant.id]);
  useEffect(() => {
    fetchCsmData();
  }, [fetchCsmData]);

  // ── Grant single CSM access ───────────────────────────────────────────────
  const handleGrantAccess = async () => {
    if (!grantEmail.trim()) return;
    setGrantLoading(true);
    setRevokeError(null);
    setBulkError(null);
    setGrantError(null);
    setRevokeResult(null);
    setBulkResult(null);
    setGrantResult(null);
    try {
      const token = await getTokenForHeimdallAdmin(idToken);
      if (!token) throw new Error('Not authenticated');
      const result = await grantCSMTenantAccess(token, tenant.id, grantEmail.trim());
      setGrantResult(result);
      setGrantEmail('');
      setShowGrantInput(false);
      await fetchCsmData();
    } catch (err) {
      setGrantError(err instanceof Error ? err.message : 'Failed to grant access');
    } finally {
      setGrantLoading(false);
    }
  };

  // ── Grant all CSMs access ─────────────────────────────────────────────────
  const handleGrantBulkAccess = async () => {
    setBulkLoading(true);
    setBulkResult(null);
    setRevokeResult(null);
    setGrantResult(null);
    setGrantEmail('');
    setRevokeError(null);
    setBulkError(null);
    setGrantError(null);
    try {
      const token = await getTokenForHeimdallAdmin(idToken);
      if (!token) throw new Error('Not authenticated');
      const result = await grantAllCSMsTenantAccess(token, tenant.id);
      setBulkResult(result);
      await fetchCsmData();
    } catch (err) {
      setBulkResult(null);
      setBulkError(err instanceof Error ? err.message : 'Failed to bulk grant access');
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Grant single CSM access ───────────────────────────────────────────────
  const handleRevokeAccess = async (csm_email: string) => {
    if (!csm_email.trim()) return;
    setRevokeLoading(true);
    setRevokeError(null);
    setBulkError(null);
    setGrantError(null);
    setBulkResult(null);
    setGrantResult(null);
    setRevokeResult(null);
    try {
      const token = await getTokenForHeimdallAdmin(idToken);
      if (!token) throw new Error('Not authenticated');
      const result = await revokeCSMTenantAccess(token, tenant.id, csm_email.trim());
      setRevokeResult(result);
      await fetchCsmData();
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : 'Failed to revoke access');
      setRevokeResult(null);
      setBulkResult(null);
    } finally {
      setRevokeLoading(false);
    }
  };

  // ── Form handlers ─────────────────────────────────────────────────────────
  const handleChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };
  const setBanner = (index: number, value: string) => {
    setFormData(prev => {
      const next = [...prev.banners];
      next[index] = value;
      return {
        ...prev,
        banners: next
      };
    });
    setError(null);
  };
  const addBanner = () => {
    setFormData(prev => ({
      ...prev,
      banners: [...prev.banners, '']
    }));
    setError(null);
  };
  const removeBanner = (index: number) => {
    setFormData(prev => ({
      ...prev,
      banners: prev.banners.filter((_, i) => i !== index)
    }));
    setError(null);
  };
  const handleSubmit = async () => {
    const validationError = validateRemoteStorage(formData.remote_storage_type, formData.remote_storage_url);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);
      const payload = sanitizeBannersAndRemoteStorage({
        ...formData
      });
      const updated = await onSubmit(tenant.id, payload);
      onSuccess(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
    } finally {
      setIsSubmitting(false);
    }
  };
  const isMultiTenant = tenant.organization?.auth_architecture === 'cognito-multi-tenant';

  // ── react-select options for the "Give Access to a CSM" dropdown ──────────
  const csmSelectOptions = useMemo(() => availableActiveCsmList.map(u => ({
    value: u.email,
    label: u.email
  })), [availableActiveCsmList]);
  return <div className="flex flex-row gap-6" data-sentry-component="EditTenantForm" data-sentry-source-file="EditTenantForm.tsx">
      {/* ── Left: tenant form ─────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col gap-6 [&_input]:!text-white [&_[data-slot='value']]:!text-white">
        {error && <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4 text-red-300 text-sm">
            {error}
          </div>}

        <div className="relative z-10 bg-slate-800/50 backdrop-blur-xl rounded-lg border border-purple-700/50 p-6 space-y-6">
          <h2 className="text-xl font-semibold text-purple-300 mb-4">Tenant Information</h2>
          <Input label="Tenant Name" name="tenant_name" value={formData.tenant_name} onValueChange={v => handleChange('tenant_name', v)} isRequired labelPlacement="outside" placeholder="e.g., City of Springfield" classNames={{
          input: 'box-border text-white',
          inputWrapper: 'box-border'
        }} data-sentry-element="Input" data-sentry-source-file="EditTenantForm.tsx" />
          {isMultiTenant && <Input label="Karpel Friendly Name" aria-label="Karpel Friendly Name" name="multi_tenant_friendly_name" value={formData.multi_tenant_friendly_name} onValueChange={v => handleChange('multi_tenant_friendly_name', v)} labelPlacement="outside" placeholder="e.g., idcassia" classNames={{
          input: 'box-border text-white',
          inputWrapper: 'box-border'
        }} />}
          <label className="flex text-white/100 font-light tracking-wider text-[14px] m-0 mb-2 p-0">
            Time Zone
          </label>
          <Select classNamePrefix="react-select" aria-label="Time Zone (optional)" styles={{
          ...TzSelectStyles,
          menu: (provided, state) => ({
            ...(TzSelectStyles.menu?.(provided, state) ?? provided),
            zIndex: 9999,
            color: 'black'
          })
        }} value={formData.timezone ? {
          value: formData.timezone,
          label: TIMEZONE_OPTIONS.find(o => o.value === formData.timezone)?.label ?? formData.timezone
        } : null} onChange={(newValue: unknown) => {
          const option = newValue as {
            value: string;
            label: string;
          } | null;
          handleChange('timezone', option?.value ?? '');
        }} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" options={TIMEZONE_OPTIONS.map(option => ({
          value: option.value,
          label: option.label
        }))} noOptionsMessage={() => 'No options available'} data-sentry-element="Select" data-sentry-source-file="EditTenantForm.tsx" />
        </div>

        <TenantBannersAndRemoteStorage banners={formData.banners} onBannerChange={setBanner} onAddBanner={addBanner} onRemoveBanner={removeBanner} remoteStorageType={formData.remote_storage_type} remoteStorageUrl={formData.remote_storage_url ?? ''} onRemoteStorageTypeChange={type => setFormData(prev => ({
        ...prev,
        remote_storage_type: type,
        ...(type == null ? {
          remote_storage_url: ''
        } : {})
      }))} onRemoteStorageUrlChange={url => handleChange('remote_storage_url', url)} onClearError={() => setError(null)} data-sentry-element="TenantBannersAndRemoteStorage" data-sentry-source-file="EditTenantForm.tsx" />

        <div className="flex justify-end gap-4 pt-2">
          <Button type="button" variant="light" onPress={onCancel} className="text-slate-300 hover:text-white" data-sentry-element="Button" data-sentry-source-file="EditTenantForm.tsx">
            Cancel
          </Button>
        <Button type="button" onPress={handleSubmit} isLoading={isSubmitting} isDisabled={!formData.tenant_name?.trim()} className="bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg" data-sentry-element="Button" data-sentry-source-file="EditTenantForm.tsx">
          Save Changes
        </Button>
      </div>
    </div>

      {/* ── Right: CSM access panel ──────────────────────────────────────── */}
      <div className="w-72 shrink-0 flex flex-col gap-4">
        {/* CSM list */}
        <div className="bg-slate-800/50 rounded-lg border border-purple-700/50 p-4 flex flex-col gap-3 flex-1 min-h-0">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wider">
              CSM Access
            </h3>
            <button type="button" onClick={fetchCsmData} className="text-xs text-slate-400 hover:text-slate-200 transition-colors" aria-label="Refresh CSM access list">
              Refresh
            </button>
          </div>

          {csmLoading ? <div className="flex items-center justify-center py-6">
              <Spinner size="sm" color="secondary" />
            </div> : csmError ? <p className="text-xs text-red-400">{csmError}</p> : assignedCsmList.length === 0 ? <p className="text-xs text-slate-500 italic py-4 text-center">
              No CSMs have access yet.
            </p> : <>

            {/* Bulk result summary */}

            <ul className="w-full flex flex-col p-0 gap-1 overflow-y-auto max-h-60 space-y-0.5 custom-scrollbar2">
              {assignedCsmList.map(csm => <li key={csm.id} className="flex items-center gap-2 text-xs py-1 px-1 group min-w-0">
                  <span className="truncate text-slate-200 min-w-0 flex-1">{csm.email}</span>
                  <button type="button" onClick={() => handleRevokeAccess(csm.email)} disabled={revokeLoading || bulkLoading} aria-label={`Revoke access for ${csm.email}`} className="shrink-0 text-slate-500 hover:text-red-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <XCircleIcon size={16} weight="fill" />
                  </button>
                </li>)}
            </ul>
            </>}

          {/* Bulk result summary */}
          {bulkResult && <div className="mt-1 rounded-md bg-slate-700/50 border border-slate-600/50 p-2 text-xs">
              <p className="text-slate-300 font-medium mb-1">Bulk grant complete</p>
              <p className="text-green-400">{bulkResult.granted} granted</p>
              {bulkResult.skipped > 0 && <p className="text-slate-400">{bulkResult.skipped} already active</p>}
              {bulkResult.failed > 0 && <p className="text-red-400">{bulkResult.failed} failed</p>}
            </div>}
          {revokeResult && <div className="mt-1 rounded-md bg-slate-700/50 border border-slate-600/50 p-2 text-xs">
                <p className="text-slate-300 font-medium mb-1">Revoke complete</p>
                <p className="text-green-400">Access for {revokeResult.email} revoked</p>
              </div>}
          {grantResult && <div className="mt-1 rounded-md bg-slate-700/50 border border-slate-600/50 p-2 text-xs">
                <p className="text-slate-300 font-medium mb-1">Grant complete</p>
                <p className="text-green-400">Access for {grantResult.email} granted</p>
              </div>}
          {bulkError && <p className="text-xs text-red-400">{bulkError}</p>}
          {grantError && <p className="text-xs text-red-400">{grantError}</p>}
          {revokeError && <p className="text-xs text-red-400 -mt-1">{revokeError}</p>}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-3">
          {/* Give all CSMs access */}
          <Button type="button" onPress={handleGrantBulkAccess} isLoading={bulkLoading} isDisabled={bulkLoading || grantLoading} startContent={!bulkLoading ? <UsersIcon size={16} /> : undefined} className="w-full bg-purple-700/60 hover:bg-purple-600/70 text-white text-sm font-medium rounded-lg border border-purple-600/40" data-sentry-element="Button" data-sentry-source-file="EditTenantForm.tsx">
            Grant Access to All CSMs
          </Button>

          {/* Give access to a single CSM */}
          {showGrantInput ? <div className="flex flex-col gap-2 bg-slate-800/50 rounded-lg border border-purple-700/50 p-3">
              <label className="text-slate-300 text-xs font-medium">Select CSM</label>
              <Select classNamePrefix="react-select" aria-label="Select CSM" placeholder="Choose a CSM…" options={csmSelectOptions} value={grantEmail ? {
            value: grantEmail,
            label: grantEmail
          } : null} onChange={opt => {
            const selected = opt as {
              value: string;
              label: string;
            } | null;
            setGrantEmail(selected?.value ?? '');
            setGrantError(null);
          }} styles={{
            ...(TzSelectStyles as any),
            menu: (provided: object) => ({
              ...provided,
              zIndex: 9999,
              color: 'black'
            })
          }} menuPlacement="top" noOptionsMessage={() => csmLoading ? 'Loading…' : 'No available CSMs'} />

              <div className="flex gap-2 mt-1">
                <Button type="button" size="sm" onPress={handleGrantAccess} isLoading={grantLoading} isDisabled={!grantEmail.trim() || grantLoading} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg">
                  Grant
                </Button>
                <Button type="button" size="sm" variant="light" onPress={() => {
              setShowGrantInput(false);
              setGrantEmail('');
              setGrantError(null);
            }} isDisabled={grantLoading} className="flex-1 text-slate-300 text-xs">
                  Cancel
                </Button>
              </div>
            </div> : <Button type="button" onPress={() => setShowGrantInput(true)} isDisabled={bulkLoading} startContent={<UserIcon size={16} />} className="w-full bg-slate-700/60 hover:bg-slate-600/70 text-white text-sm font-medium rounded-lg border border-slate-600/40">
              Grant Access to a CSM
            </Button>}
        </div>
      </div>
    </div>;
}